import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Checkbox,
  Badge,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  List,
  Divider,
  Modal,
  Tag,
  Alert,
  Spin,
  message
} from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { BatchInfo, InspectionReport } from '../types';
import { getInspectionBatches, getBatchReport, deleteBatches } from '../services/api';
import { StatusCalculationEngine } from '../services/StatusCalculationEngine';
import StatusIndicator from './StatusIndicator';
import BatchDeleteConfirmModal from './BatchDeleteConfirmModal';

const { Text } = Typography;

interface BatchWithStats extends BatchInfo {
  hasReviewed: boolean;
  normalCount: number;
  warningCount: number;
  dangerCount: number;
  reviewedCount: number;
}

interface BatchPreviewData {
  batch: BatchWithStats;
  reports: InspectionReport[];
  statistics: {
    total: number;
    normal: number;
    warning: number;
    danger: number;
    reviewed: number;
  };
  sampleRecords: InspectionReport[];
}

interface BatchManagerProps {
  onBatchesSelected?: (batchIds: string[]) => void;
  selectedBatchIds?: string[];
  showActions?: boolean;
  onDeleteBatches?: (batchIds: string[]) => void;
  onRefreshBatches?: () => void;
}

const BatchManager: React.FC<BatchManagerProps> = ({
  onBatchesSelected,
  selectedBatchIds = [],
  showActions = true,
  onDeleteBatches,
  onRefreshBatches
}) => {
  const [batches, setBatches] = useState<BatchWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<BatchPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [internalSelectedBatchIds, setInternalSelectedBatchIds] = useState<string[]>([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deletingBatchIds, setDeletingBatchIds] = useState<string[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 使用内部状态或外部传入的选中状态
  const currentSelectedBatchIds = selectedBatchIds.length > 0 ? selectedBatchIds : internalSelectedBatchIds;

  // 解析参考范围的工具函数
  const parseReferenceRange = (referenceRange: string): { min?: number; max?: number } => {
    if (!referenceRange || referenceRange === '-') {
      return {};
    }

    const rangeMatch = referenceRange.match(/(\d+(?:\.\d+)?)\s*[-~～]\s*(\d+(?:\.\d+)?)/);
    if (rangeMatch) {
      return {
        min: parseFloat(rangeMatch[1]),
        max: parseFloat(rangeMatch[2])
      };
    }

    const maxOnlyMatch = referenceRange.match(/[≤<=]\s*(\d+(?:\.\d+)?)/);
    if (maxOnlyMatch) {
      return {
        max: parseFloat(maxOnlyMatch[1])
      };
    }

    const minOnlyMatch = referenceRange.match(/[≥>=]\s*(\d+(?:\.\d+)?)/);
    if (minOnlyMatch) {
      return {
        min: parseFloat(minOnlyMatch[1])
      };
    }

    const numberMatch = referenceRange.match(/^\d+(?:\.\d+)?$/);
    if (numberMatch) {
      return {
        max: parseFloat(referenceRange)
      };
    }

    return {};
  };

  // 计算批次统计信息
  const calculateBatchStats = (reports: InspectionReport[]): Omit<BatchWithStats, keyof BatchInfo> => {
    let normalCount = 0;
    let warningCount = 0;
    let dangerCount = 0;
    let reviewedCount = 0;

    reports.forEach(report => {
      // 检查是否已审核
      if (report.evaluation && report.evaluation.trim() !== '') {
        reviewedCount++;
      }

      // 计算智能状态
      try {
        const { min, max } = parseReferenceRange(report.reference_range);
        const statusResult = StatusCalculationEngine.calculate({
          actualValue: report.actual_value,
          referenceMin: min,
          referenceMax: max,
          elementName: report.element_name,
          cityLevel: report.city_level
        });

        switch (statusResult.status) {
          case 'normal':
            normalCount++;
            break;
          case 'slightly_high':
          case 'slightly_low':
            warningCount++;
            break;
          case 'dangerous':
          case 'significantly_low':
            dangerCount++;
            break;
        }
      } catch (error) {
        // 如果计算失败，使用原有的warning_status
        switch (report.warning_status) {
          case 'normal':
            normalCount++;
            break;
          case 'warning':
            warningCount++;
            break;
          case 'danger':
            dangerCount++;
            break;
        }
      }
    });

    return {
      hasReviewed: reviewedCount > 0,
      normalCount,
      warningCount,
      dangerCount,
      reviewedCount
    };
  };

  // 加载批次列表
  const loadBatches = async () => {
    setLoading(true);
    try {
      const batchList = await getInspectionBatches();

      // 为每个批次加载报告并计算统计信息
      const batchesWithStats: BatchWithStats[] = [];

      for (const batch of batchList) {
        try {
          const batchReport = await getBatchReport(batch.batch_id);
          const stats = calculateBatchStats(batchReport.reports);

          batchesWithStats.push({
            ...batch,
            ...stats
          });
        } catch (error) {
          console.error(`加载批次 ${batch.batch_id} 统计信息失败:`, error);
          // 如果加载失败，使用默认值
          batchesWithStats.push({
            ...batch,
            hasReviewed: false,
            normalCount: 0,
            warningCount: 0,
            dangerCount: 0,
            reviewedCount: 0
          });
        }
      }

      setBatches(batchesWithStats);
    } catch (error) {
      console.error('加载批次列表失败:', error);
      message.error('加载批次列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理批次预览
  const handlePreview = async (batch: BatchWithStats) => {
    setPreviewLoading(true);
    setPreviewVisible(true);

    try {
      const batchReport = await getBatchReport(batch.batch_id);

      // 计算统计信息
      const statistics = {
        total: batchReport.reports.length,
        normal: batch.normalCount,
        warning: batch.warningCount,
        danger: batch.dangerCount,
        reviewed: batch.reviewedCount
      };

      // 获取前5条样本记录
      const sampleRecords = batchReport.reports.slice(0, 5);

      setPreviewData({
        batch,
        reports: batchReport.reports,
        statistics,
        sampleRecords
      });
    } catch (error) {
      console.error('加载批次预览数据失败:', error);
      message.error('加载批次预览数据失败');
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 处理单个批次选择
  const handleBatchSelection = (batchId: string, checked: boolean) => {
    let newSelectedBatchIds: string[];

    if (checked) {
      newSelectedBatchIds = [...currentSelectedBatchIds, batchId];
    } else {
      newSelectedBatchIds = currentSelectedBatchIds.filter(id => id !== batchId);
    }

    if (selectedBatchIds.length > 0) {
      // 如果使用外部状态，调用回调
      onBatchesSelected?.(newSelectedBatchIds);
    } else {
      // 使用内部状态
      setInternalSelectedBatchIds(newSelectedBatchIds);
    }
  };

  // 处理全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    const newSelectedBatchIds = checked ? batches.map(batch => batch.batch_id) : [];

    if (selectedBatchIds.length > 0) {
      onBatchesSelected?.(newSelectedBatchIds);
    } else {
      setInternalSelectedBatchIds(newSelectedBatchIds);
    }
  };

  // 处理批次删除确认
  const handleDeleteConfirm = () => {
    if (currentSelectedBatchIds.length === 0) {
      message.warning('请先选择要删除的批次');
      return;
    }

    setDeletingBatchIds(currentSelectedBatchIds);
    setDeleteConfirmVisible(true);
  };

  // 执行批次删除
  const handleDeleteExecute = async (reason?: string) => {
    setDeleteLoading(true);
    try {
      // 调用真实的删除API
      const result = await deleteBatches(deletingBatchIds, reason);

      // 清空选择状态
      if (selectedBatchIds.length > 0) {
        onBatchesSelected?.([]);
      } else {
        setInternalSelectedBatchIds([]);
      }

      // 重新加载数据
      await loadBatches();

      // 显示删除结果
      if (result.summary.failure > 0) {
        message.warning(
          `删除完成：成功 ${result.summary.success} 个，失败 ${result.summary.failure} 个，共删除 ${result.summary.totalDeletedRecords} 条记录`
        );
      } else {
        message.success(
          `成功删除 ${result.summary.success} 个批次，共删除 ${result.summary.totalDeletedRecords} 条记录`
        );
      }

      // 调用外部删除回调通知
      if (onDeleteBatches) {
        onDeleteBatches(deletingBatchIds);
      }
    } catch (error) {
      console.error('删除批次失败:', error);
      message.error('删除批次失败，请重试');
      throw error; // 重新抛出错误，让模态框显示错误状态
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmVisible(false);
      setDeletingBatchIds([]);
    }
  };

  // 取消删除确认
  const handleDeleteCancel = () => {
    setDeleteConfirmVisible(false);
    setDeletingBatchIds([]);
  };

  // 获取批次状态指示器
  const getBatchStatusIndicator = (batch: BatchWithStats) => {
    const totalCount = batch.normalCount + batch.warningCount + batch.dangerCount;
    const reviewProgress = totalCount > 0 ? (batch.reviewedCount / totalCount * 100).toFixed(1) : '0';

    return (
      <Space direction="vertical" size="small">
        <Space size="small">
          <Badge count={batch.dangerCount} style={{ backgroundColor: '#ff4d4f' }} />
          <Badge count={batch.warningCount} style={{ backgroundColor: '#faad14' }} />
          <Badge count={batch.normalCount} style={{ backgroundColor: '#52c41a' }} />
        </Space>

        {batch.hasReviewed && (
          <Tooltip title={`已审核 ${batch.reviewedCount}/${totalCount} 条记录 (${reviewProgress}%)`}>
            <Tag color="blue" icon={<CheckCircleOutlined />}>
              审核中 {reviewProgress}%
            </Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  // 批次表格列定义
  const columns: TableColumnsType<BatchWithStats> = [
    {
      title: (
        <Checkbox
          indeterminate={currentSelectedBatchIds.length > 0 && currentSelectedBatchIds.length < batches.length}
          checked={batches.length > 0 && currentSelectedBatchIds.length === batches.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          批次选择
        </Checkbox>
      ),
      dataIndex: 'batch_id',
      width: 120,
      render: (batchId: string) => (
        <Checkbox
          checked={currentSelectedBatchIds.includes(batchId)}
          onChange={(e) => handleBatchSelection(batchId, e.target.checked)}
        />
      ),
    },
    {
      title: '批次ID',
      dataIndex: 'batch_id',
      width: 200,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>{text}</Text>
      ),
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: '导入时间',
      dataIndex: 'import_date',
      width: 150,
      render: (text: string) => (
        <Text type="secondary">{new Date(text).toLocaleDateString()}</Text>
      ),
    },
    {
      title: '记录数量',
      dataIndex: 'record_count',
      width: 100,
      align: 'center',
      render: (count: number) => (
        <Badge count={count} showZero style={{ backgroundColor: '#108ee9' }} />
      ),
    },
    {
      title: '状态统计',
      key: 'status',
      width: 180,
      render: (_, record) => getBatchStatusIndicator(record),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="预览批次详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
              size="small"
            />
          </Tooltip>

          <Tooltip title="查看报告">
            <Button
              type="link"
              icon={<FileTextOutlined />}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 统计信息
  const totalStats = useMemo(() => {
    return batches.reduce(
      (acc, batch) => ({
        totalBatches: acc.totalBatches + 1,
        totalRecords: acc.totalRecords + batch.record_count,
        reviewedBatches: acc.reviewedBatches + (batch.hasReviewed ? 1 : 0),
        totalNormal: acc.totalNormal + batch.normalCount,
        totalWarning: acc.totalWarning + batch.warningCount,
        totalDanger: acc.totalDanger + batch.dangerCount,
      }),
      {
        totalBatches: 0,
        totalRecords: 0,
        reviewedBatches: 0,
        totalNormal: 0,
        totalWarning: 0,
        totalDanger: 0,
      }
    );
  }, [batches]);

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* 批次统计卡片 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="总批次数"
              value={totalStats.totalBatches}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="总记录数"
              value={totalStats.totalRecords}
              prefix={<InfoCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="已审核批次"
              value={totalStats.reviewedBatches}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="正常记录"
              value={totalStats.totalNormal}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="预警记录"
              value={totalStats.totalWarning}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="危险记录"
              value={totalStats.totalDanger}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 操作栏 */}
      {showActions && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadBatches();
                onRefreshBatches?.();
              }}
            >
              刷新批次
            </Button>

            {currentSelectedBatchIds.length > 0 && (
              <>
                <Text type="secondary">
                  已选择 {currentSelectedBatchIds.length} 个批次
                </Text>

                {onDeleteBatches && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteConfirm}
                  >
                    删除选中批次
                  </Button>
                )}
              </>
            )}
          </Space>
        </Card>
      )}

      {/* 批次列表 */}
      <Card
        title="批次管理"
        extra={
          <Space>
            <Text type="secondary">共 {batches.length} 个批次</Text>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={batches}
          rowKey="batch_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 批次预览模态框 */}
      <Modal
        title="批次详情预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        <Spin spinning={previewLoading}>
          {previewData && (
            <div>
              {/* 基本信息 */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="批次ID"
                      value={previewData.batch.batch_id}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="公司名称"
                      value={previewData.batch.company_name}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="导入时间"
                      value={new Date(previewData.batch.import_date).toLocaleDateString()}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="记录总数"
                      value={previewData.statistics.total}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                </Row>
              </Card>

              {/* 状态统计 */}
              <Card size="small" title="状态统计" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="正常"
                      value={previewData.statistics.normal}
                      valueStyle={{ color: '#52c41a' }}
                      suffix={`(${((previewData.statistics.normal / previewData.statistics.total) * 100).toFixed(1)}%)`}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="预警"
                      value={previewData.statistics.warning}
                      valueStyle={{ color: '#faad14' }}
                      suffix={`(${((previewData.statistics.warning / previewData.statistics.total) * 100).toFixed(1)}%)`}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="危险"
                      value={previewData.statistics.danger}
                      valueStyle={{ color: '#ff4d4f' }}
                      suffix={`(${((previewData.statistics.danger / previewData.statistics.total) * 100).toFixed(1)}%)`}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="已审核"
                      value={previewData.statistics.reviewed}
                      valueStyle={{ color: '#1890ff' }}
                      suffix={`(${((previewData.statistics.reviewed / previewData.statistics.total) * 100).toFixed(1)}%)`}
                    />
                  </Col>
                </Row>

                {previewData.batch.hasReviewed && (
                  <Alert
                    message="此批次包含已审核记录"
                    description="部分记录已经过人工审核，删除操作需要谨慎处理"
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}
              </Card>

              {/* 样本记录 */}
              <Card size="small" title="样本记录（前5条）">
                <List
                  dataSource={previewData.sampleRecords}
                  renderItem={(record, index) => (
                    <List.Item key={record.id}>
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text>{record.company_name}</Text>
                            <Text type="secondary">-</Text>
                            <Text>{record.business_name}</Text>
                            <Text type="secondary">-</Text>
                            <Text>{record.element_name}</Text>
                          </Space>
                        }
                        description={
                          <Space>
                            <Text>检查结果: {record.actual_value}</Text>
                            <Text>参考范围: {record.reference_range}</Text>
                            <StatusIndicator
                              actualValue={record.actual_value}
                              referenceRange={record.reference_range}
                              elementName={record.element_name}
                              cityLevel={record.city_level}
                              evaluation={record.evaluation}
                              size="small"
                            />
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />

                {previewData.statistics.total > 5 && (
                  <Divider>
                    <Text type="secondary">
                      还有 {previewData.statistics.total - 5} 条记录未显示
                    </Text>
                  </Divider>
                )}
              </Card>
            </div>
          )}
        </Spin>
      </Modal>

      {/* 批次删除确认模态框 */}
      <BatchDeleteConfirmModal
        visible={deleteConfirmVisible}
        batches={batches.filter(batch => deletingBatchIds.includes(batch.batch_id))}
        onConfirm={handleDeleteExecute}
        onCancel={handleDeleteCancel}
        loading={deleteLoading}
      />
    </div>
  );
};

export default BatchManager;